import { Table, Button, Select, Tooltip, Checkbox, message, Modal, Form, TableProps, Radio, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table/interface'
import { StarOutlined, SendOutlined } from '@ant-design/icons'
import Axios from 'axios'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'

export interface NotificationType {
    notification_type_id: string,
    label: string,
    key: string,
    description: string,
    enabled: boolean
}

export interface FacturationAccount {
    key: number,
    coll_id: string,
    email: string,
    create_date: Date,
    rank: number,
    name: string,
    price?: number,
    facturable: boolean,
    notifications: NotificationType[],
    access_to_facturation_tab?: boolean,
    super_admin?: boolean,
    ms_pro?: boolean,
    ex_facture?: boolean
}

type Account = FacturationAccount

// Memoized components for better performance
const FacturableCheckbox = ({ facturable, loading, onChange }: {
    facturable: boolean,
    loading: boolean,
    onChange: (checked: boolean) => void
}) => (
    <Checkbox
        checked={facturable}
        onChange={(e) => onChange(e.target.checked)}
        disabled={loading}
    />
);

const NotificationSelect = ({ notifications, loading, onChange }: {
    notifications: NotificationType[],
    loading: boolean,
    onChange: (value: string[]) => void
}) => {
    const enabledValues = useMemo(
        () => notifications.filter(n => n.enabled).map(n => n.notification_type_id),
        [notifications]
    );

    return (
        <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Notifications activées"
            optionLabelProp="label"
            maxTagCount={1}
            loading={loading}
            value={enabledValues}
            onChange={onChange}
        >
            {notifications.map(n => (
                <Select.Option value={n.notification_type_id} label={n.label} key={n.notification_type_id}>
                    <Tooltip placement='left' title={n.description}>
                        <span>{n.label}</span>
                    </Tooltip>
                </Select.Option>
            ))}
        </Select>
    );
};

/** Email template key sent to the backend: default | prosperences | msPro */
type CredentialsTrame = 'default' | 'prosperences' | 'msPro'

function defaultTrameForAccounts(accounts: Account[]): CredentialsTrame {
    if (accounts.length > 0 && accounts.every(a => a.ms_pro)) return 'msPro'
    return 'default'
}

export default function FacturationUsers({
    billingStatusView = 'factures',
    accounts,
    setAccounts,
    loading = false
}: {
    billingStatusView?: 'factures' | 'ex'
    accounts: FacturationAccount[]
    setAccounts: React.Dispatch<React.SetStateAction<FacturationAccount[]>>
    loading?: boolean
}) {
    const [massUpdateModalVisible, setMassUpdateModalVisible] = useState(false);
    const [massUpdateForm] = Form.useForm();
    const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
    const [sendCredentialsModalVisible, setSendCredentialsModalVisible] = useState(false);
    const [sendCredentialsLoading, setSendCredentialsLoading] = useState(false);
    const [credentialsTrame, setCredentialsTrame] = useState<CredentialsTrame>('default');

    const uniqueNames = useMemo(
        () => [...new Set(accounts.map(a => a.name))],
        [accounts]
    )
    const uniqueEmails = useMemo(
        () => [...new Set(accounts.map(a => a.email))],
        [accounts]
    )

    const filteredAccounts = useMemo(
        () => accounts.filter(a => billingStatusView === 'ex' ? !!a.ex_facture : !a.ex_facture),
        [accounts, billingStatusView]
    )

    const allNotificationTypes = useMemo(() => {
        return Array.from(
            new Map(
                accounts.flatMap(acc => acc.notifications || [])
                    .map(n => [n.notification_type_id, n])
            ).values()
        );
    }, [accounts]);

    useEffect(() => {
        setSelectedAccounts([])
    }, [billingStatusView])

    // *********************** States ***********************
    const [collaboratorsLoading, setCollaboratorsLoading] = useState<Record<string, boolean>>({})

    const isMountedRef = useRef(true)

    // *********************** Effects ***********************
    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // --- Helper: safely update a single account in state ---
    const updateAccountInState = useCallback((coll_id: string, partial: Partial<Account>) => {
        setAccounts(prev => {
            const index = prev.findIndex(a => a.coll_id === coll_id);
            if (index === -1) return prev;
            const updated = [...prev];
            updated[index] = { ...prev[index], ...partial };
            return updated;
        });
    }, [setAccounts]);

    // --- Helper: safely mark/unmark loading ---
    const setLoading = useCallback((coll_id: string, val: boolean) => {
        setCollaboratorsLoading((prev: Record<string, boolean>) => (val ? { ...prev, [coll_id]: true } : (() => {
            const { [coll_id]: _, ...rest } = prev;
            return rest;
        })()));
    }, []);

    const rowSelection: TableProps<Account>['rowSelection'] = {
        columnWidth: 50,
        onChange: (selectedRowKeys: React.Key[], selectedRows: Account[]) => {
            setSelectedAccounts(selectedRows);
        },
    };

    const updateNotifications = useCallback(async (account: Account, enabledNotificationIds: string[]) => {
        if (!isMountedRef.current) return;

        const originalNotifications = account.notifications;
        const collId = account.coll_id;

        const updatedNotifications = account.notifications?.map(n => ({
            ...n,
            enabled: enabledNotificationIds.includes(n.notification_type_id as string)
        }));

        // Immediate UI update
        updateAccountInState(collId, { notifications: updatedNotifications });
        setLoading(collId, true);

        try {
            const disabledNotificationIds = account.notifications
                .filter(n => !enabledNotificationIds.includes(n.notification_type_id as string))
                .map(n => n.notification_type_id);

            await Axios.post('/cnegoces/updateAccountNotifications',
                {
                    coll_id: collId,
                    enabledNotificationIds: enabledNotificationIds,
                    disabledNotificationIds: disabledNotificationIds
                }
            );
        } catch (error) {
            console.error('Failed to update notifications:', error);
            // Revert on error
            updateAccountInState(collId, { notifications: originalNotifications });
        } finally {
            if (isMountedRef.current) {
                setLoading(collId, false);
            }
        }
    }, [updateAccountInState, setLoading]);

    const performMassUpdate = useCallback(async (updateType: 'notifications', value: string[]) => {
        if (selectedAccounts.length === 0) {
            console.warn('No accounts selected');
            return;
        }

        const updates = selectedAccounts.map(account => {
            const originalNotifications = account.notifications;
            const collId = account.coll_id;

            const updatedNotifications = account.notifications?.map(n => ({
                ...n,
                enabled: value.includes(n.notification_type_id as string)
            }));

            const disabledNotificationIds = account.notifications
                .filter(n => !value.includes(n.notification_type_id as string))
                .map(n => n.notification_type_id);

            return {
                coll_id: collId,
                originalNotifications: originalNotifications,
                updateNotifications: updatedNotifications,
                enabledNotificationIds: value,
                disabledNotificationIds: disabledNotificationIds
            }
        });

        // Set loading for all selected accounts
        updates.forEach(account => {
            setLoading(account.coll_id, true)
            updateAccountInState(account.coll_id, { notifications: account.updateNotifications });
        });

        try {
            await Axios.post('/cnegoces/updateMultipleAccountsNotifications', {
                updates
            });
        } catch (error) {
            console.error('Mass update failed:', error);
            // Revert all accounts on error
            updates.forEach(account => {
                updateAccountInState(account.coll_id, { notifications: account.originalNotifications });
            });
        } finally {
            // Remove loading state for all accounts
            updates.forEach(account => setLoading(account.coll_id, false));
        }
    }, [selectedAccounts, setLoading, updateAccountInState]);

    const sendCredentials = useCallback(async () => {
        if (selectedAccounts.length === 0) return;

        setSendCredentialsLoading(true);
        try {
            // Either msPro: true or trame: 'prosperences'; otherwise neither
            const payload: { coll_ids: string[]; msPro?: boolean; trame?: string } = {
                coll_ids: selectedAccounts.map(account => account.coll_id)
            };
            if (credentialsTrame === 'msPro') {
                payload.msPro = true;
            } else if (credentialsTrame === 'prosperences') {
                payload.trame = 'prosperences';
            }

            const response = await Axios.post('/collaborators/sendCredentials', payload);
            const { summary } = response.data;
            if (summary.errors > 0) {
                message.warning(
                    `${summary.sent} identifiant(s) envoyé(s), ${summary.skipped} ignoré(s), ${summary.errors} erreur(s)`
                );
            } else {
                message.success(`${summary.sent} identifiant(s) envoyé(s) avec succès`);
            }
            setSendCredentialsModalVisible(false);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.error
                || error?.response?.data?.summary
                    ? `${error.response.data.summary.sent} envoyé(s), ${error.response.data.summary.errors} erreur(s)`
                    : "Erreur lors de l'envoi des identifiants";
            message.error(errorMessage);
        } finally {
            setSendCredentialsLoading(false);
        }
    }, [selectedAccounts, credentialsTrame]);

    // *********************** Memoized Columns ***********************

    // Dynamic columns that depend on state
    const columns = useMemo<ColumnsType<Account>>(() => [
        {
            title: 'Email',
            dataIndex: 'email',
            filterSearch: true,
            filters: uniqueEmails.map(email => ({ text: email, value: email })),
            onFilter: (value: any, record) => record.email.includes(value),
        },
        {
            title: 'Date de création',
            dataIndex: 'create_date',
            width: 125,
            defaultSortOrder: 'descend',
            render: (date: Date) => date.toLocaleDateString(),
            sorter: (a, b) => a.create_date.getTime() - b.create_date.getTime(),
        },
        {
            title: 'Rang',
            dataIndex: 'rank',
            width: 100,
            sorter: (a, b) => a.rank - b.rank,
            render: (rank: number) => rank.toString(),
            filters: [
                { text: '1', value: 1 },
                { text: '2', value: 2 },
                { text: '3', value: 3 },
                { text: '4', value: 4 },
            ],
            onFilter: (value, record) => record.rank === value,
        },
        {
            title: 'Orga',
            dataIndex: 'name',
            filters: uniqueNames.map(name => ({ text: name, value: name })),
            onFilter: (value: any, record) => record.name.includes(value),
            sorter: (a, b) => a.name.localeCompare(b.name),
            filterSearch: true,
            render: (name: string) => <Tooltip title={name}>{name}</Tooltip>
        },
        {
            title: "Type d'outil",
            dataIndex: 'ms_pro',
            width: 120,
            filters: [
                { text: 'Scan & Conquest', value: false },
                { text: 'Ms Pro', value: true },
            ],
            onFilter: (value, record) => Boolean(record.ms_pro) === value,
            render: (msPro: boolean) => (
                <Tag color={msPro ? 'green' : 'blue'}>{msPro ? 'MsPro' : 'S&C'}</Tag>
            ),
        },
        {
            title: 'Prix',
            dataIndex: 'price',
            width: 100,
            sorter: (a, b) => (a.price || 0) - (b.price || 0),
        },
        {
            title: 'Facturé',
            dataIndex: 'facturable',
            width: 100,
            render: (facturable: boolean, account: Account) => (
                <FacturableCheckbox
                    facturable={facturable}
                    loading={!!collaboratorsLoading[account.coll_id]}
                    onChange={(checked) => axios.post("/save/collsFacturation", [
                        {
                            coll_id: account.coll_id,
                            super_admin: account.super_admin,
                            billed: checked,
                            access_to_facturation_tab: account.access_to_facturation_tab
                        }
                    ]).then(() => {
                        message.success("Modifications enregistrées")
                        setAccounts(accounts.map(e => {
                            if (e.coll_id === account.coll_id) {
                                e.facturable = checked
                            }
                            return e
                        }))
                    }, (_) => {
                        message.error("Erreur lors de l'enregistrement des modifications")
                    })}
                />
            ),
            onFilter: (value, record) => record.facturable === value,
            filters: [
                { text: 'Oui', value: true },
                { text: 'Non', value: false }
            ],
        },
        {
            title: 'Notifications actives',
            dataIndex: 'notifications',
            render: (notifications: NotificationType[], account: Account) => (
                <NotificationSelect
                    notifications={notifications}
                    loading={!!collaboratorsLoading[account.coll_id]}
                    onChange={(value) => updateNotifications(account, value)}
                />
            ),
        },
        {
            title: 'Super Admin',
            dataIndex: 'super_admin',
            width: 100,
            filters: [{ text: 'Oui', value: true }, { text: 'Non', value: false }],
            onFilter: (value, record) => record.super_admin === value,
            key: 'super_admin',
            render: (super_admin: boolean, record) => <Checkbox checked={super_admin} onChange={(value) => {
                axios.post("/save/collsFacturation", [
                    {
                        coll_id: record.coll_id,
                        super_admin: value.target.checked,
                        billed: record.facturable,
                        access_to_facturation_tab: record.access_to_facturation_tab
                    }
                ]).then(() => {
                    message.success("Modifications enregistrées")
                    setAccounts(accounts.map(e => {
                        if (e.coll_id === record.coll_id) {
                            e.super_admin = value.target.checked
                        }
                        return e
                    }))
                }, (_) => {
                    message.error("Erreur lors de l'enregistrement des modifications")
                })

            }} />
        }
    ], [uniqueEmails, uniqueNames, collaboratorsLoading, updateNotifications, accounts, setAccounts]);

    return (
        <>
            {/* Modification de masse (notifications) */}
            <Tooltip
                title={selectedAccounts.length === 0 ? "Sélectionnez au moins un compte (via la case à cocher) pour activer la modification de masse" : "Modifier les notifications pour tous les comptes sélectionnés"}
            >
                <span>
                    <Button
                        style={{ marginLeft: 8 }}
                        type="primary"
                        shape="round"
                        icon={<StarOutlined />}
                        size="middle"
                        onClick={() => setMassUpdateModalVisible(true)}
                        disabled={selectedAccounts.length === 0}
                    >
                        Modification de masse (notifications)
                    </Button>
                </span>
            </Tooltip>
            {/* Envoi des identifiants */}
            <Tooltip
                title={selectedAccounts.length === 0 ? "Sélectionnez au moins un compte (via la case à cocher) pour activer l'envoi des identifiants" : "Réinitialiser les mots de passe et envoyer les identifiants par email aux comptes sélectionnés"}
            >
                <span>
                    <Button
                        style={{ marginLeft: 8 }}
                        type="primary"
                        shape="round"
                        icon={<SendOutlined />}
                        size="middle"
                        onClick={() => {
                            setCredentialsTrame(defaultTrameForAccounts(selectedAccounts));
                            setSendCredentialsModalVisible(true);
                        }}
                        disabled={selectedAccounts.length === 0}
                        loading={sendCredentialsLoading}
                    >
                        Envoi des identifiants
                    </Button>
                </span>
            </Tooltip>
            <Modal
                title={`Envoi des identifiants (${selectedAccounts.length} compte(s) sélectionné(s))`}
                open={sendCredentialsModalVisible}
                onCancel={() => setSendCredentialsModalVisible(false)}
                onOk={sendCredentials}
                okText="Confirmer l'envoi"
                cancelText="Annuler"
                confirmLoading={sendCredentialsLoading}
            >
                <p>
                    Cette action va <strong>réinitialiser le mot de passe</strong> de chaque compte sélectionné
                    (sauf les comptes SSO) et envoyer les identifiants par email à chaque utilisateur.
                </p>
                <div style={{ marginBottom: 16 }}>
                    <p style={{ marginBottom: 8 }}>Trame d'email :</p>
                    <Radio.Group
                        value={credentialsTrame}
                        onChange={(e) => setCredentialsTrame(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <Radio.Button value="default">Scan & Conquest</Radio.Button>
                        <Radio.Button value="msPro">Ms Pro</Radio.Button>
                        <Radio.Button value="prosperences">Prosperences</Radio.Button>
                    </Radio.Group>
                </div>
                <p style={{ fontSize: '12px', color: '#666' }}>
                    Comptes concernés : {selectedAccounts.map(a => a.email).join(', ')}
                </p>
            </Modal>
            <Modal
                title={`Modification de masse (${selectedAccounts.length} comptes sélectionnés)`}
                open={massUpdateModalVisible}
                onCancel={() => {
                    setMassUpdateModalVisible(false);
                    massUpdateForm.resetFields();
                }}
                onOk={() => {
                    massUpdateForm.validateFields().then(values => {
                        const notificationsToSet = values.notifications ?? [];
                        performMassUpdate('notifications', notificationsToSet);
                        setMassUpdateModalVisible(false);
                        massUpdateForm.resetFields();
                    });
                }}
                okText="Valider"
                cancelText="Annuler"
            >
                <Form form={massUpdateForm} layout="vertical">
                    <Form.Item
                        name="notifications"
                        label="Notifications à activer/désactiver pour tous les comptes sélectionnés"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Sélectionner les notifications à activer"
                            style={{ width: '100%' }}
                        >
                            {allNotificationTypes.map(n => (
                                <Select.Option
                                    value={n.notification_type_id}
                                    key={n.notification_type_id}
                                >
                                    <Tooltip title={n.description}>
                                        {n.label}
                                    </Tooltip>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                        Note: Les notifications non sélectionnées seront désactivées pour tous les comptes.
                    </div>
                </Form>
            </Modal>
            <br />
            <br />
            <Table
                pagination={false}
                columns={columns}
                dataSource={filteredAccounts}
                loading={loading}
                scroll={{ y: 500 }}
                virtual
                rowKey="coll_id"
                rowSelection={rowSelection}
            />
        </>)
}